// privacy.js
// Single source of truth for how much analytics (Amplitude) may run in this
// browser. There are three modes:
//
//   'full'     — the visitor answered the age prompt as 13+: persistent
//                random ID, attribution, and session replay.
//   'baseline' — everyone else (under 13, "No thanks", or hasn't answered):
//                anonymous events only. No persistent ID (a throwaway one per
//                page load, nothing written to storage), no session replay,
//                no attribution storage, no IP. This keeps play/page totals
//                accurate without tracking anyone across visits.
//   'off'      — the visitor turned analytics off completely on the privacy
//                page. Nothing is sent.
//
// We only ever store the resulting status, never the birth year itself.

const STATUS_KEY = 'topamine_privacy_status'; // 'adult' | 'minor' | 'declined'
const OFF_KEY = 'topamine_analytics_off';
const ANALYTICS_STORAGE_KEYS = ['topamine_user_id', 'topamine_traffic_source'];
export const PRIVACY_EVENT = 'topamine:privacy-change';
export const GAME_COMPLETED_EVENT = 'topamine:game-completed';
const GAME_DONE_KEY = 'topamine_game_completed';

export const MINIMUM_AGE = 13;

export function getPrivacyStatus() {
    try {
        return localStorage.getItem(STATUS_KEY);
    } catch (e) {
        return null;
    }
}

export function isAnalyticsOff() {
    try {
        return localStorage.getItem(OFF_KEY) === '1';
    } catch (e) {
        return false;
    }
}

export function getAnalyticsMode() {
    if (isAnalyticsOff()) return 'off';
    return getPrivacyStatus() === 'adult' ? 'full' : 'baseline';
}

// Conservative: someone born in year Y turns 13 at some point during Y+13, so
// they're only certainly 13+ once Y+14 has begun. An ambiguous answer always
// lands on the safe (baseline) side.
export function isOldEnough(year, now = new Date()) {
    const y = typeof year === 'string' && year.trim() === '' ? NaN : Number(year);
    if (!Number.isInteger(y) || y < 1900 || y > now.getFullYear()) return false;
    return now.getFullYear() >= y + MINIMUM_AGE + 1;
}

// Remove everything the full mode put in this browser (our IDs plus
// Amplitude's own AMP_* storage and cookies).
function purgeAnalyticsStorage() {
    try {
        ANALYTICS_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));
        Object.keys(localStorage)
            .filter((k) => k.startsWith('AMP_') || k.startsWith('amp_'))
            .forEach((k) => localStorage.removeItem(k));
    } catch (e) { /* storage unavailable */ }
    try {
        document.cookie.split(';').forEach((c) => {
            const name = c.split('=')[0].trim();
            if (name.startsWith('AMP_') || name.startsWith('amp_')) {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
            }
        });
    } catch (e) { /* ignore */ }
}

function notify() {
    try {
        window.dispatchEvent(new CustomEvent(PRIVACY_EVENT));
    } catch (e) { /* ignore */ }
}

function setStatus(status) {
    try {
        localStorage.setItem(STATUS_KEY, status);
    } catch (e) { /* storage unavailable: stays unset, which means baseline */ }
    if (status !== 'adult') purgeAnalyticsStorage();
    notify();
}

// Returns the resulting status.
export function submitBirthYear(year) {
    const status = isOldEnough(year) ? 'adult' : 'minor';
    setStatus(status);
    return status;
}

export function declineAnalytics() {
    // Never let a 'minor' answer be replaced by something re-promptable.
    if (getPrivacyStatus() === 'minor') return 'minor';
    setStatus('declined');
    return 'declined';
}

// Re-show the age prompt after "No thanks". Not available once someone has
// answered as under 13.
export function reopenPrompt() {
    if (getPrivacyStatus() === 'minor') return false;
    try {
        localStorage.removeItem(STATUS_KEY);
    } catch (e) { /* ignore */ }
    notify();
    return true;
}

export function setAnalyticsOff() {
    try {
        localStorage.setItem(OFF_KEY, '1');
    } catch (e) { /* ignore */ }
    purgeAnalyticsStorage();
    notify();
}

export function setAnalyticsOn() {
    try {
        localStorage.removeItem(OFF_KEY);
    } catch (e) { /* ignore */ }
    notify();
}

// The age prompt waits until the visitor has finished a game, so it never
// competes with the first-visit rules popup. This is just a local flag that
// never leaves the device.
export function recordGameCompleted() {
    try {
        if (localStorage.getItem(GAME_DONE_KEY) === '1') return;
        localStorage.setItem(GAME_DONE_KEY, '1');
    } catch (e) { /* ignore */ }
    try {
        window.dispatchEvent(new CustomEvent(GAME_COMPLETED_EVENT));
    } catch (e) { /* ignore */ }
}

export function hasCompletedGame() {
    try {
        return localStorage.getItem(GAME_DONE_KEY) === '1';
    } catch (e) {
        return false;
    }
}
